import {
    MeshBuilder,
    StandardMaterial,
    Texture,
    Vector3,
} from "@babylonjs/core"
import { TextBlock } from "@babylonjs/gui"
import { activateApp } from "../../../common/AppsRouter"
import { AppType } from "../../../common/AppType"
import { log } from "../../../common/Logging"
import { TransitionState } from "../../../phaser/GameState"
import { MapState } from "../../../phaser/map/MapState"
import { BabUtils } from "../../BabUtils"
import { MazeAudio, SoundLoader } from "./assets/MazeAudio"
import { mazeImages } from "./assets/MazeImages"
import { CameraType } from "./Cameras"
import { guiScreenPositions } from "./Gui"
import { MazeCigs } from "./MazeCigs"
import { MazeRunner } from "./MazeRunner"
import { MazeStore } from "./MazeStore"
import { mazeBuilders } from "./MazeWalls"

export class MazeStages {
    startMaze = async () => {
        const { cameras, gui } = MazeStore.instance
        const { intro } = MazeAudio.instance

        await cameras.autoRotateCamera(false)

        const introAudio = await intro(false)
        introAudio.play()

        const bulletBuilder = gui.createBulletBuilder(guiScreenPositions.first)
        bulletBuilder.addBullet(
            3000,
            "You’ll see how hard it is to escape addiction.",
        )
        bulletBuilder.addBullet(
            10000,
            "Being addicted to tobacco is like being trapped in a maze.",
        )
        bulletBuilder.addBullet(
            15000,
            "The goal is to get out of the maze before getting addicted.",
        )
        bulletBuilder.addBullet(
            19000,
            "First, you’ll see two teens go through the maze.",
        )
        bulletBuilder.addBullet(
            27000,
            "Then you’ll have a chance to try for yourself.",
        )
        bulletBuilder.start()

        introAudio.onEndedObservable.addOnce(() => {
            gui.addNextButton(() => {
                this.startExample1()
                gui.removeBullets()
            })
        })
    }

    startExample1 = async () => {
        const { cameras, gui } = MazeStore.instance
        const {
            example1,
            teenFinishedInOneMinuteRight,
            teenFinishedInOneMinuteWrong,
            tryExampleAgain,
        } = MazeAudio.instance

        MazeStore.instance.mazeCigs = MazeCigs.forMazeRunner()

        await cameras.autoRotateCamera(true)

        gui.addTeen(false)

        gui.createTimer(60)
        const runner = MazeRunner.nonUser()
        const next = async () => {
            await runner.run()
            try {
                let audio: SoundLoader
                if (MazeStore.instance.teenCanFinishOneMinute) {
                    audio = teenFinishedInOneMinuteRight
                } else {
                    audio = teenFinishedInOneMinuteWrong
                }
                await audio()
            } catch (error) {
                log.debug(error)
            } finally {
                await tryExampleAgain()
                gui.addNextButton(() => {
                    runner.disposeRunner()
                    gui.disposeTeen()
                    this.startExample2()
                })
            }
        }

        const nextRadButton = gui.addRadioOptions(
            "Do you think a teen who has never used tobacco will get through the maze in one minute?",
            [
                {
                    name: "Yes",
                    result: () => {
                        MazeStore.instance.teenCanFinishOneMinute = true
                        next()
                    },
                },
                {
                    name: "No",
                    result: () => {
                        MazeStore.instance.teenCanFinishOneMinute = false
                        next()
                    },
                },
            ],
        )

        await example1()
        nextRadButton.isEnabled = true
    }

    startExample2 = async () => {
        const { cameras, gui } = MazeStore.instance
        const {
            example2,
            teenFinishedInOneMinuteRight2,
            teenFinishedInOneMinuteWrong2,
            yourTurn,
        } = MazeAudio.instance

        await cameras.autoRotateCamera(true)

        gui.addTeen(true)
        gui.createTimer(60)

        const runner = MazeRunner.user()
        const next = async () => {
            await runner.run()
            try {
                let audio: SoundLoader
                if (MazeStore.instance.teenCanFinishOneMinute) {
                    audio = teenFinishedInOneMinuteWrong2
                } else {
                    audio = teenFinishedInOneMinuteRight2
                }
                await audio()
            } catch (error) {
                log.debug(error)
            } finally {
                await yourTurn()
                gui.addNextButton(() => {
                    runner.disposeRunner()
                    gui.disposeTeen()
                    this.userMazePreamble()
                })
            }
        }

        const nextRadButton = gui.addRadioOptions(
            "Do you think a teen who has tried tobacco will get through the maze in one minute?",
            [
                {
                    name: "Yes",
                    result: () => {
                        MazeStore.instance.teenCanFinishOneMinute = true
                        next()
                    },
                },
                {
                    name: "No",
                    result: () => {
                        MazeStore.instance.teenCanFinishOneMinute = false
                        next()
                    },
                },
            ],
        )

        await example2()
        nextRadButton.isEnabled = true
    }

    userMazePreamble = async () => {
        const { gui } = MazeStore.instance
        const { youGetTwoMinutes } = MazeAudio.instance

        await this.mustDoBeforeMaze()

        const nextRadButton = gui.addRadioOptions(
            "Imagine that you have started experimenting with smoking or vaping. Do you think you can avoid " +
                "addiction and get through the maze in two minutes?",
            [
                {
                    name: "Yes",
                    result: () => {
                        MazeStore.instance.youCanFinishTwoMinutes = true
                        this.userMazePreamble2()
                    },
                },
                {
                    name: "No",
                    result: () => {
                        MazeStore.instance.youCanFinishTwoMinutes = false
                        this.userMazePreamble2()
                    },
                },
            ],
            260,
            guiScreenPositions.second,
        )

        await youGetTwoMinutes()

        nextRadButton.isEnabled = true
    }

    userMazePreamble2 = async () => {
        const { cameras, gui, mazeWalls } = MazeStore.instance
        const { startMaze } = MazeAudio.instance

        const bulletBuilder = gui.createBulletBuilder()
        bulletBuilder.addBullet(
            2000,
            "You can see what it’s like to be inside the maze.",
        )
        bulletBuilder.addBullet(
            6000,
            "Watch out for the cigarettes and e-cigarettes",
        )
        bulletBuilder.addBullet(
            11000,
            "Use the arrows to move forward, backward, left and right.",
        )
        bulletBuilder.addBullet(
            15000,
            "You can click on the arrows or you can use the arrow keys on your keyboard.",
        )
        bulletBuilder.start()

        gui.setShowMoveButtons(true)

        await startMaze()

        gui.addNextButton(async () => {
            gui.removeBullets()
            await cameras.moveIntoMaze()
            await mazeWalls.movePortcullis(true)
            this.startUserMaze()
        }, "Start")
    }

    startUserMaze = async (performPreMaze = false) => {
        const { cameras, gui } = MazeStore.instance

        if (performPreMaze) {
            // Started right here in debug mode, so do setup that is otherwise already done.
            await this.mustDoBeforeMaze(true)
        }

        cameras.addFreeCameraInputs()
        MazeStore.instance.userStartedMaze = true

        gui.onTimerComplete = this.mazeOver
        gui.startTimer()
    }

    mazeOver = async () => {
        const { cameras, gui, youCanFinishTwoMinutes } = MazeStore.instance
        const {
            wrongCouldntGetOut,
            rightCouldntGetOut,
            wasItHarderThanThought,
            mazeLookedEasy,
            mazeWasHard,
        } = MazeAudio.instance

        MazeStore.instance.userDoneWithMaze = true
        gui.stopTimer()
        gui.stopSmokeTimer()
        cameras.removeFreeCameraInputs()

        let text: TextBlock
        let audio: SoundLoader
        if (youCanFinishTwoMinutes) {
            text = gui.addGuiText(
                "You were wrong! Even though you thought you would get out of the maze, you couldn’t because it was really hard!",
            )
            audio = wrongCouldntGetOut
        } else {
            text = gui.addGuiText(
                "You were right! You couldn’t get out of the maze in time.",
            )
            audio = rightCouldntGetOut
        }
        await audio()
        text.dispose()
        gui.disposeTimer()

        const escape = () =>
            gui.addNextButton(() => {
                MapState.goTo = TransitionState.MAZE_EXIT
                activateApp(AppType.PHASER)
            })

        const bulletBuilder = gui.createBulletBuilder(guiScreenPositions.first)

        const nextRadButton = gui.addRadioOptions(
            "Was it harder to get out of the maze than you thought?",
            [
                {
                    name: "Yes",
                    result: async () => {
                        bulletBuilder.addBullet(
                            0,
                            "The maze looked easy when you watched other people.",
                        )
                        bulletBuilder.addBullet(
                            7000,
                            "It was harder for you to get out than you thought.",
                        )
                        bulletBuilder.addBullet(
                            11000,
                            "You might think, “I can try smoking or vaping, and I won’t get addicted.",
                        )
                        bulletBuilder.addBullet(
                            17000,
                            "You are no different than anyone else.",
                        )
                        bulletBuilder.addBullet(
                            21000,
                            "You can easily become addicted.",
                        )
                        bulletBuilder.addBullet(
                            24000,
                            "Bad things can happen to you, too!",
                        )
                        bulletBuilder.start()

                        await mazeLookedEasy()
                        escape()
                    },
                },
                {
                    name: "No",
                    result: async () => {
                        bulletBuilder.addBullet(
                            0,
                            "The maze was hard for everyone, including you.",
                        )
                        bulletBuilder.addBullet(
                            3000,
                            "It’s the same with addiction.",
                        )
                        bulletBuilder.addBullet(
                            6000,
                            "You might think, “I can try smoking or vaping, and I won’t get addicted.",
                        )
                        bulletBuilder.addBullet(
                            11000,
                            "You are no different than anyone else.",
                        )
                        bulletBuilder.addBullet(
                            15500,
                            "You can easily become addicted.",
                        )
                        bulletBuilder.start()

                        await mazeWasHard()
                        escape()
                    },
                },
            ],
            120,
        )

        await wasItHarderThanThought()
        nextRadButton.isEnabled = true
    }

    realMazeTopView = () => {
        const { mazeWalls, mazeCigs } = MazeStore.instance
        const mazeCigsWalls = mazeBuilders.realMaze()
        mazeWalls.destroy()
        mazeCigs.destroy()
        MazeStore.instance.mazeWalls = mazeCigsWalls.walls
        MazeStore.instance.mazeCigs = mazeCigsWalls.cigs
    }

    private mustDoBeforeMaze = async (performPreMaze = false) => {
        const { cameras, gui, mazeCigs, scene } = MazeStore.instance
        let mazeWalls = MazeStore.instance.mazeWalls

        const entranceSign = MeshBuilder.CreatePlane(
            "entranceSign",
            { width: 5, height: 5 },
            scene,
        )
        entranceSign.addRotation(0, BabUtils.degreeToRadian(-20), 0)
        entranceSign.position = new Vector3(-12, 2.25, -7)

        const entranceMaterial = new StandardMaterial("entranceMaterial", scene)
        entranceMaterial.diffuseTexture = new Texture(
            mazeImages.entranceSign,
            scene,
        )
        entranceMaterial.diffuseTexture.hasAlpha = true
        entranceSign.material = entranceMaterial

        await cameras.autoRotateCamera(false)
        cameras.useCamera(CameraType.FREE)
        cameras.removeFreeCameraInputs()
        gui.setShowMoveButtons(false)

        gui.createTimer(120)
        await mazeWalls.movePortcullis(true)

        const mazeCigsWalls = mazeBuilders.realMaze()
        mazeWalls.destroy()
        if (mazeCigs) {
            mazeCigs.destroy()
        }
        MazeStore.instance.mazeWalls = mazeCigsWalls.walls
        MazeStore.instance.mazeCigs = mazeCigsWalls.cigs
        MazeStore.instance.mazePathfinder = mazeCigsWalls.pathFinder

        mazeWalls = MazeStore.instance.mazeWalls

        await mazeWalls.movePortcullis(false)

        if (performPreMaze) {
            await cameras.moveIntoMaze()
            await mazeWalls.movePortcullis(true)
        }
    }
}
